import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, Github } from 'lucide-react';
import Logo from './Logo';
import { API } from '@/App';
import axios from 'axios';

const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const currentYear = new Date().getFullYear();

  // Kurumsal künye satırı (sadece dolu alanlar gösterilir)
  const legalLine = [
    settings?.tax_office && `Vergi Dairesi: ${settings.tax_office}`,
    settings?.tax_number && `VKN: ${settings.tax_number}`,
    settings?.mersis_no && `MERSİS No: ${settings.mersis_no}`,
    settings?.trade_registry_no && `Ticaret Sicil No: ${settings.trade_registry_no}`,
  ].filter(Boolean).join(' · ');

  return (
    <footer className="bg-[#0a0a0f] border-t border-white/10" data-testid="main-footer">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <Logo size="sm" />
              <span className="text-xl font-bold">Ener Yazılım</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              {settings?.footer_description || "Yazılım, elektronik, bilişim güvenlik ve teknoloji alanında yenilikçi çözümler sunuyoruz."}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Hızlı Bağlantılar</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/hakkimizda" className="text-white/60 hover:text-[#FF6B35] transition-colors text-sm">
                  Hakkımızda
                </Link>
              </li>
              <li>
                <Link to="/hizmetler" className="text-white/60 hover:text-[#FF6B35] transition-colors text-sm">
                  Hizmetler
                </Link>
              </li>
              <li>
                <Link to="/projeler" className="text-white/60 hover:text-[#FF6B35] transition-colors text-sm">
                  Projeler
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/60 hover:text-[#FF6B35] transition-colors text-sm">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">İletişim</h3>
            <ul className="space-y-3">
              {settings?.company_email && (
                <li className="flex items-center space-x-2 text-white/60 text-sm">
                  <Mail size={16} className="text-[#FF6B35]" />
                  <span>{settings.company_email}</span>
                </li>
              )}
              {settings?.company_phone && (
                <li className="flex items-center space-x-2 text-white/60 text-sm">
                  <Phone size={16} className="text-[#FF6B35]" />
                  <span>{settings.company_phone}</span>
                </li>
              )}
              {settings?.company_address && (
                <li className="flex items-start space-x-2 text-white/60 text-sm">
                  <MapPin size={16} className="text-[#FF6B35] mt-1" />
                  <span>{settings.company_address}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Sosyal Medya</h3>
            <div className="flex space-x-3">
              {settings?.social_links?.linkedin && (
                <a
                  href={settings.social_links.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                  data-testid="social-linkedin"
                >
                  <Linkedin size={20} />
                </a>
              )}
              {settings?.social_links?.twitter && (
                <a
                  href={settings.social_links.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                  data-testid="social-twitter"
                >
                  <Twitter size={20} />
                </a>
              )}
              {settings?.social_links?.instagram && (
                <a
                  href={settings.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram size={20} />
                </a>
              )}
              {settings?.social_links?.github && (
                <a
                  href={settings.social_links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                  data-testid="social-github"
                >
                  <Github size={20} />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 text-center space-y-1">
          {settings?.legal_name && (
            <p className="text-white/70 text-xs font-medium" data-testid="footer-legal-name">
              {settings.legal_name}
            </p>
          )}
          {settings?.legal_name && settings?.company_address && (
            <p className="text-white/50 text-xs" data-testid="footer-legal-address">
              {settings.company_address}
            </p>
          )}
          {legalLine && (
            <p className="text-white/50 text-xs" data-testid="footer-legal-ids">
              {legalLine}
            </p>
          )}
          <p className="text-white/60 text-sm pt-2">
            {settings?.footer_copyright
              ? settings.footer_copyright.replace(/\b20\d{2}\b(?!.*\b20\d{2}\b)/, String(currentYear))
              : `© ${currentYear} Ener Yazılım. Tüm hakları saklıdır.`}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;